import { useDepartmentService } from '@/api/services/departmentService';
import { BaseDecarbFooterBar } from '@/decarbonization/components/BaseDecarbFooterBar';
import { usePencilsDown } from '@/decarbonization/hooks/usePencilsDown.ts';
import { useOrg } from '@/organization/useOrg';
import { useOrganizationPerms } from '@/organization/useOrganizationRole';
import { useEnumHelpers } from '@/queries/useEnumHelpers';
import { useGetProject } from '@/queries/useProjects';
import {
  useGetProjectSubprojects,
  useGetSubproject,
} from '@/queries/useSubprojects';
import { fetchOrganizationUsers } from '@/services/userService';
import { useSubprojectStore } from '@/stores/SubprojectStore';
import { ManagerialBlock, MultiSelect } from '@/tools/ManagerialComponents';
import { SubprojectAddressField } from '@/tools/SubprojectAddressField';
import {
  buildAddressOptions,
  SUBPROJECT_ADDRESS_MAX_LENGTH,
} from '@/tools/subprojectAddress';
import type { Department } from '@/types/department';
import type { Member } from '@/types/user';
import { PermissionModal } from '@/users/ProfilePageComponents';
import { Save } from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  Container,
  Grow,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { lighten } from '@mui/system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TransitionGroup } from 'react-transition-group';

export function SubprojectDetails() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const pencilsDown = usePencilsDown();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedOrg } = useOrg();
  const subprojectStore = useSubprojectStore();
  const departmentService = useDepartmentService();
  const { projectId, subId } = useParams();
  const permsQuery = useOrganizationPerms(selectedOrg?.id);

  // get project and subproject data
  const { data: fetchedProject } = useGetProject(selectedOrg?.id, projectId);
  const { data: fetchedSubproject } = useGetSubproject(
    selectedOrg?.id,
    subId || '',
  );
  const { data: projectSubprojects } = useGetProjectSubprojects(
    selectedOrg?.id,
    projectId,
  );

  // form fields
  const { sectors } = useEnumHelpers();
  const [name, setName] = useState('');
  const [sector, setSector] = useState('Building');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [userSubprojId, setUserSubprojId] = useState('');
  const [nameError, setNameError] = useState('');
  const [idError, setIdError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  // Address dropdown: the project's address plus the other subprojects'.
  const addressOptions = useMemo(
    () =>
      buildAddressOptions(
        fetchedProject?.address,
        projectSubprojects ?? [],
        subId,
      ),
    [fetchedProject?.address, projectSubprojects, subId],
  );
  // A new subproject starts with the project's address, and follows it (a
  // background refetch may bring a newer one) until the user types, picks or
  // clears — from then on their value is never overwritten.
  const addressEditedRef = useRef(false);

  // edit modal fields
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [sponsoringDeptIds, setSponsoringDeptIds] = useState<string[]>([]);
  const [overseeingDeptIds, setOverseeingDeptIds] = useState<string[]>([]);
  const [usingDeptIds, setUsingDeptIds] = useState<string[]>([]);
  const [progManagerIds, setProgManagerIds] = useState<string[]>([]);
  const [projManagerIds, setProjManagerIds] = useState<string[]>([]);
  const [budgetOfficerIds, setBudgetOfficerIds] = useState<string[]>([]);

  // Reused colors / font sizes
  const SUBBOX_COLOR = alpha(theme.palette.landingPage.activeLight, 0.35);
  const SUBTITLE_BAR_COLOR = 'primary.main';
  const INPUT_BOX_COLOR = 'landingPage.activeLight';
  const MAINBOX_COLOR = 'background.card';
  const RESPONSIVE_TEXT_FONT = 'clamp(10px, 0.9vw, 13px)';

  useEffect(() => {
    if (subId || !fetchedProject || addressEditedRef.current) return;
    const projectAddress = fetchedProject.address?.trim() ?? '';
    // too long to save as a subproject address: start empty rather than
    // pre-fill a value the server rejects
    setAddress(
      projectAddress.length <= SUBPROJECT_ADDRESS_MAX_LENGTH
        ? projectAddress
        : '',
    );
  }, [subId, fetchedProject]);

  const handleSaveSubproject = async (
    data: {
      name: string;
      userSubprojId: string;
      address: string;
      description: string;
      sector: string;
      sponsoringDepts: string[];
      overseeingDepts: string[];
      usingDepts: string[];
      programManagers: string[];
      projectManagers: string[];
      budgetOfficers: string[];
    },
    backToSummary: boolean,
  ) => {
    // Error checking
    if (isSavingRef.current) return;
    if (!selectedOrg || !projectId) return;
    if (!name) {
      setNameError('Name is required');
    }
    if (!userSubprojId) {
      setIdError('Subproject ID is required');
    }
    if (!name || !userSubprojId) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);

    try {
      if (subId) {
        await subprojectStore.update({
          ...data,
          id: subId,
          projectId: projectId,
        });
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['subproject', subId],
            refetchType: 'all',
          }),
          queryClient.invalidateQueries({
            queryKey: ['projects-with-subprojects'],
            refetchType: 'all',
          }),
          queryClient.invalidateQueries({
            queryKey: ['project-subprojects'],
            // mark stale only: this page unmounts after save, and the next
            // one to mount refetches
            refetchType: 'none',
          }),
        ]);

        if (backToSummary) {
          navigate(`/project/${projectId}/proj-details`);
        } else {
          // Navigate to location selection page
          navigate(
            `/project/${projectId}/site-selection?subprojectId=${subId}`,
          );
        }
      } else {
        const newSubproject = await subprojectStore.new({
          ...data,
          projectId: projectId,
          organizationId: selectedOrg.id,
        });
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['projects'],
            refetchType: 'all',
          }),
          queryClient.invalidateQueries({
            queryKey: ['projects-with-subprojects'],
            refetchType: 'all',
          }),
          queryClient.invalidateQueries({
            queryKey: ['project-subprojects'],
            // mark stale only: this page unmounts after save, and the next
            // one to mount refetches
            refetchType: 'none',
          }),
        ]);
        if (backToSummary) {
          navigate(`/project/${projectId}/proj-details`);
        } else {
          // Navigate to location selection page
          navigate(
            `/project/${projectId}/site-selection?subprojectId=${newSubproject.id}`,
          );
        }
      }
    } catch (error) {
      if (subId) {
        console.error('Failed to update subproject:', error);
      } else {
        console.error('Failed to create subproject', error);
      }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  // get department / user options for managerial page
  const departmentsQuery = useQuery({
    queryKey: ['departments', selectedOrg?.id],
    queryFn: async () => {
      const response = await departmentService.list();
      return response.data; // Extract data from ApiResponse
    },
    enabled: !!selectedOrg,
  });

  const usersQuery = useQuery({
    queryKey: [
      'organization-users',
      selectedOrg?.id,
      1,
      '', // search
      false, // showArchived
      '', // department
    ],
    queryFn: () =>
      fetchOrganizationUsers({
        organizationId: selectedOrg?.id || '',
        page: 1,
        search: '',
        showArchived: false,
        department: '',
      }),
    enabled: !!selectedOrg,
  });

  const availableDepartments: Department[] = departmentsQuery.data || [];
  const availableMembers: Member[] = usersQuery.data?.results || [];

  useEffect(() => {
    if (fetchedSubproject) {
      setName(fetchedSubproject.name);
      setUserSubprojId(fetchedSubproject.userSubprojId);
      setAddress(fetchedSubproject.address);
      setDescription(fetchedSubproject.description);
      setSector(fetchedSubproject.sector);
      setSponsoringDeptIds(fetchedSubproject.sponsoringDepts);
      setOverseeingDeptIds(fetchedSubproject.overseeingDepts);
      setUsingDeptIds(fetchedSubproject.usingDepts);
      setProgManagerIds(fetchedSubproject.programManagers);
      setProjManagerIds(fetchedSubproject.projectManagers);
      setBudgetOfficerIds(fetchedSubproject.budgetOfficers);
      setNameError('');
      setIdError('');
    } else if (fetchedProject) {
      setSponsoringDeptIds(fetchedProject.sponsoringDepts || []);
      setOverseeingDeptIds(fetchedProject.overseeingDepts || []);
      setUsingDeptIds(fetchedProject.usingDepts || []);
      setProgManagerIds(fetchedProject.programManagers || []);
      setProjManagerIds(fetchedProject.projectManagers || []);
      setBudgetOfficerIds(fetchedProject.budgetOfficers || []);
    }
  }, [subId, fetchedSubproject, fetchedProject]);

  if (permsQuery.isLoading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant={'h5'}>Loading....</Typography>
      </Box>
    );
  }

  if (!selectedOrg) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant={'h5'}>
          Please select an organization from the user menu to access this
          project.
        </Typography>
      </Box>
    );
  }

  if (!permsQuery.data?.canManageProjs) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant={'h5'}>
          You do not have permission to create subprojects.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Title of page */}
        <Box
          sx={{
            mt: '35px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            pointerEvents: 'auto',
            ml: '70px',
            flexShrink: 0,
          }}
        >
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            component='h1'
            sx={{
              fontFamily: 'Futura PT, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(24px, 1.85vw, 35px)',
              color: 'rgba(40, 40, 40, 1)',
              textShadow: {
                xs: '-2px 2px rgba(101, 166, 178, 1)',
                lg: '-4px 3px rgba(101, 166, 178, 1)',
              },
              textTransform: 'uppercase',
              letterSpacing: 'clamp(8px, 0.75vw, 14px)',
              lineHeight: 1,
            }}
          >
            Subproject Creation
          </Typography>
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            component='h2'
            sx={{
              fontFamily: 'Futura PT, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(45px, 3.5vw, 68px)',
              color: 'rgba(40, 40, 40, 1)',
              textShadow: {
                xs: '-2px 2px rgba(101, 166, 178, 1)',
                lg: '-4px 3px rgba(101, 166, 178, 1)',
              },
              textTransform: 'uppercase',
              letterSpacing: 'clamp(8px, 0.75vw, 14px)',
              lineHeight: 1,
            }}
          >
            {fetchedSubproject?.name || 'New Subproject'}
          </Typography>
        </Box>
        <Container
          maxWidth={false}
          sx={{
            width: '97%',
            maxWidth: '2400px',
            mt: 5,
            mb: 3,
            px: 10,
            display: 'flex',
            flexDirection: 'column !important',
            minHeight: 0,
            maxHeight: '85vh',
            textTransform: 'uppercase',
          }}
        >
          <Paper
            sx={{
              bgcolor: 'primary.dark',
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              p: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Typography variant={'h6'}>Subproject Overview</Typography>
              <Typography variant={'subtitle1'} sx={{ mt: '3px' }}>
                Tell us about your subproject
              </Typography>
            </Box>
          </Paper>
          <Grid
            container
            spacing={10}
            sx={{
              bgcolor: MAINBOX_COLOR,
              px: 6,
              pt: 6,
              pb: 11,
              display: 'flex',
              justifyContent: 'center',
              overflow: 'auto',
              flexGrow: 1,
              overflowY: 'auto',
              minHeight: 0,
              borderBottomLeftRadius: 8,
              borderBottomRightRadius: 8,
            }}
          >
            <Grid
              size={7}
              maxWidth={'lg'}
              sx={{
                mt: -2,
                gap: 3,
                maxHeight: '65vh',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* DETAILS SECTION */}
              <Paper
                sx={{
                  bgcolor: SUBTITLE_BAR_COLOR,
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                  p: 3,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                }}
              >
                <Typography variant={'subtitle2'}>
                  Subproject Details
                </Typography>
              </Paper>
              <Box
                sx={{
                  bgcolor: SUBBOX_COLOR,
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  p: 3,
                  pb: 5,
                  mt: -3,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  flexWrap: 'wrap',
                }}
              >
                <Stack width='100%'>
                  <Box sx={{ mt: 5, display: 'flex', gap: 4 }}>
                    <Paper
                      sx={{
                        bgcolor: INPUT_BOX_COLOR,
                        borderRadius: 2,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        minWidth: '40%',
                      }}
                    >
                      <Typography
                        variant={'subtitle2'}
                        sx={{ fontSize: 11, mb: 1, color: 'primary.main' }}
                      >
                        Subproject Name
                      </Typography>
                      <TextField
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value);
                          if (nameError) setNameError('');
                        }}
                        fullWidth
                        required
                        error={!!nameError}
                        helperText={nameError}
                        autoFocus
                        variant='standard'
                        sx={{
                          '& .MuiInputBase-input': { color: 'primary.main' },
                        }}
                      />
                    </Paper>
                    <Paper
                      sx={{
                        bgcolor: INPUT_BOX_COLOR,
                        borderRadius: 2,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        minWidth: '40%',
                      }}
                    >
                      <Typography
                        variant={'subtitle2'}
                        sx={{ fontSize: 11, mb: 1, color: 'primary.main' }}
                      >
                        Subproject ID
                      </Typography>
                      <TextField
                        value={userSubprojId}
                        onChange={(e) => {
                          setUserSubprojId(e.target.value);
                          if (idError) setIdError('');
                        }}
                        fullWidth
                        required
                        error={!!idError}
                        helperText={idError}
                        variant='standard'
                        sx={{
                          '& .MuiInputBase-input': { color: 'primary.main' },
                        }}
                      />
                    </Paper>
                  </Box>
                  <Paper
                    sx={{
                      bgcolor: INPUT_BOX_COLOR,
                      borderRadius: 1,
                      px: 3,
                      py: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      variant={'subtitle2'}
                      sx={{ fontSize: 11, mb: 1, color: 'primary.main' }}
                    >
                      Address
                    </Typography>
                    <SubprojectAddressField
                      value={address}
                      options={addressOptions}
                      onChange={(next) => {
                        // any user action, including re-picking the
                        // current address, stops following the project
                        addressEditedRef.current = true;
                        setAddress(next);
                      }}
                    />
                  </Paper>
                  <Paper
                    sx={{
                      bgcolor: INPUT_BOX_COLOR,
                      borderRadius: 1,
                      px: 3,
                      py: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      variant={'subtitle2'}
                      sx={{ fontSize: 11, mb: 1, color: 'primary.main' }}
                    >
                      Description
                    </Typography>
                    <TextField
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      fullWidth
                      multiline
                      rows={2}
                      variant='standard'
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 'auto',
                          minHeight: '40px',
                        },
                        '& .MuiInputBase-input': { color: 'primary.main' },
                      }}
                    />
                  </Paper>
                  {/* MANAGERIAL SECTION */}
                  <Box sx={{ display: 'flex', gap: 4, textTransform: 'none' }}>
                    <Typography
                      variant={'subtitle2'}
                      color={'primary.dark'}
                      sx={{ mt: 3, fontSize: 15, fontWeight: 700 }}
                    >
                      Managerial Overview
                    </Typography>
                    <Button
                      variant={'contained'}
                      onClick={() => setEditModalOpen(true)}
                      sx={{
                        fontSize: RESPONSIVE_TEXT_FONT,
                        mt: 4,
                        padding: 1,
                        width: '60px',
                        maxHeight: '20px',
                        boxShadow: 'none',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          boxShadow: 'none',
                        },
                        '&:active': {
                          boxShadow: 'none',
                        },
                      }}
                    >
                      Edit
                    </Button>
                  </Box>
                  <Grid
                    container
                    spacing={'2rem'}
                    sx={{ textTransform: 'none' }}
                  >
                    <ManagerialBlock
                      title={'Program Manager(s)'}
                      selectedIds={progManagerIds}
                      dropdownOptions={availableMembers}
                      fontSize={RESPONSIVE_TEXT_FONT}
                      isSubprojectPage={true}
                    />
                    <ManagerialBlock
                      title={'Budget Officer(s)'}
                      selectedIds={budgetOfficerIds}
                      dropdownOptions={availableMembers}
                      fontSize={RESPONSIVE_TEXT_FONT}
                      isSubprojectPage={true}
                    />
                    <ManagerialBlock
                      title={'Project Manager(s)'}
                      selectedIds={projManagerIds}
                      dropdownOptions={availableMembers}
                      fontSize={RESPONSIVE_TEXT_FONT}
                      isSubprojectPage={true}
                    />
                    <ManagerialBlock
                      title={'Sponsoring Department(s)'}
                      selectedIds={sponsoringDeptIds}
                      dropdownOptions={availableDepartments}
                      fontSize={RESPONSIVE_TEXT_FONT}
                      isSubprojectPage={true}
                    />
                    <ManagerialBlock
                      title={'Overseeing Department(s)'}
                      selectedIds={overseeingDeptIds}
                      dropdownOptions={availableDepartments}
                      fontSize={RESPONSIVE_TEXT_FONT}
                      isSubprojectPage={true}
                    />
                    <ManagerialBlock
                      title={'Using Department(s)'}
                      selectedIds={usingDeptIds}
                      dropdownOptions={availableDepartments}
                      fontSize={RESPONSIVE_TEXT_FONT}
                      isSubprojectPage={true}
                    />
                  </Grid>
                </Stack>
              </Box>
            </Grid>

            <Grid size={5} maxWidth={'lg'} sx={{ mt: -2 }}>
              <Paper
                sx={{
                  bgcolor: SUBTITLE_BAR_COLOR,
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                  p: 3,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                }}
              >
                <Typography variant={'subtitle2'}>
                  What type of Subproject?
                </Typography>
              </Paper>
              <Box
                component={TransitionGroup}
                sx={{
                  bgcolor: SUBBOX_COLOR,
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  display: 'flex',
                  flexWrap: 'wrap',
                  padding: 5,
                  gap: 3,
                  justifyContent: 'center',
                  '& > :not(style)': {
                    m: 1,
                    width: 'clamp(80px, 8.5vw, 130px)',
                    height: 'clamp(80px, 8.5vw, 130px)',
                    display: 'flex',
                    flexDirection: 'column',
                    textAlign: 'center',
                  },
                }}
              >
                {(Array.isArray(sectors) ? sectors : []).map((s: any) => {
                  return (
                    <Grow key={s.name}>
                      <Button
                        variant={'contained'}
                        color={'primary'}
                        disabled={pencilsDown}
                        value={s.name}
                        onClick={() => {
                          if (pencilsDown) return;
                          setSector(s.name);
                        }}
                        sx={{
                          borderRadius: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: sector === s.name ? 5 : 0,
                          borderColor: 'white',
                          p: 4,
                          width: 110,
                          height: 110,
                          '&.MuiButton-containedPrimary': {
                            backgroundColor:
                              sector === s.name
                                ? 'background.card'
                                : 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                              backgroundColor:
                                sector === s.name
                                  ? lighten(theme.palette.primary.light, 0.5)
                                  : lighten(theme.palette.primary.main, 0.15),
                            },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: 1,
                          }}
                        >
                          <Typography
                            variant={'subtitle2'}
                            color={'secondary'}
                            sx={{ mt: 1, fontSize: RESPONSIVE_TEXT_FONT }}
                          >
                            {(s as any).description || s.value || s.name}
                          </Typography>
                        </Box>
                      </Button>
                    </Grow>
                  );
                })}
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* EDIT MODAL */}
        {editModalOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1300,
            }}
          >
            <Box
              sx={{
                backgroundColor: 'landingPage.activeLight',
                maxWidth: '1200px',
                width: '90%',
                maxHeight: '90vh',
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Paper
                sx={{
                  bgcolor: 'primary.dark',
                  borderRadius: 0,
                  p: 3,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Typography variant={'h6'}>Managerial Overview</Typography>
              </Paper>
              <Grid
                container
                rowSpacing={4}
                columnSpacing={15}
                sx={{ p: 10 }}
                justifyContent={'center'}
              >
                <MultiSelect
                  title={'Program Manager(s)'}
                  onAdd={(selectedId: string) => {
                    setProgManagerIds([...progManagerIds, selectedId]);
                  }}
                  onRemove={(selectedId: string) =>
                    setProgManagerIds(
                      progManagerIds.filter((id) => id !== selectedId),
                    )
                  }
                  dropdownOptions={availableMembers.filter(
                    (member) => member.role === 'Program Manager',
                  )}
                  selectedIds={progManagerIds}
                  isDepartment={false}
                />
                <MultiSelect
                  title={'Budget Officer(s)'}
                  onAdd={(selectedId: string) =>
                    setBudgetOfficerIds([...budgetOfficerIds, selectedId])
                  }
                  onRemove={(selectedId: string) =>
                    setBudgetOfficerIds(
                      budgetOfficerIds.filter((id) => id !== selectedId),
                    )
                  }
                  dropdownOptions={availableMembers.filter(
                    (member) => member.role === 'Budget Manager',
                  )}
                  selectedIds={budgetOfficerIds}
                  isDepartment={false}
                />
                <MultiSelect
                  title={'Project Manager(s)'}
                  onAdd={(selectedId: string) =>
                    setProjManagerIds([...projManagerIds, selectedId])
                  }
                  onRemove={(selectedId: string) =>
                    setProjManagerIds(
                      projManagerIds.filter((id) => id !== selectedId),
                    )
                  }
                  dropdownOptions={availableMembers}
                  selectedIds={projManagerIds}
                  isDepartment={false}
                />
                <MultiSelect
                  title={'Sponsoring Department(s)'}
                  onAdd={(selectedId: string) =>
                    setSponsoringDeptIds([...sponsoringDeptIds, selectedId])
                  }
                  onRemove={(selectedId: string) =>
                    setSponsoringDeptIds(
                      sponsoringDeptIds.filter((id) => id !== selectedId),
                    )
                  }
                  dropdownOptions={availableDepartments}
                  selectedIds={sponsoringDeptIds}
                  isDepartment={true}
                />
                <MultiSelect
                  title={'Overseeing Department(s)'}
                  onAdd={(selectedId: string) =>
                    setOverseeingDeptIds([...overseeingDeptIds, selectedId])
                  }
                  onRemove={(selectedId: string) =>
                    setOverseeingDeptIds(
                      overseeingDeptIds.filter((id) => id !== selectedId),
                    )
                  }
                  dropdownOptions={availableDepartments}
                  selectedIds={overseeingDeptIds}
                  isDepartment={true}
                />
                <MultiSelect
                  title={'Using Department(s)'}
                  onAdd={(selectedId: string) =>
                    setUsingDeptIds([...usingDeptIds, selectedId])
                  }
                  onRemove={(selectedId: string) =>
                    setUsingDeptIds(
                      usingDeptIds.filter((id) => id !== selectedId),
                    )
                  }
                  dropdownOptions={availableDepartments}
                  selectedIds={usingDeptIds}
                  isDepartment={true}
                />
              </Grid>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mb: 7,
                  mr: 10,
                }}
              >
                <Button
                  type='submit'
                  variant='contained'
                  startIcon={<Save />}
                  onClick={() => setEditModalOpen(false)}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* WARNING MODAL */}
        {warningModalOpen && (
          <PermissionModal
            open={warningModalOpen}
            onClose={() => setWarningModalOpen(false)}
            onConfirm={() => navigate(`/project/${projectId}/proj-details`)}
            message={
              'Pressing cancel now will result in all changes made lost. \n Are you sure you would like to proceed?'
            }
          />
        )}
      </Box>

      {/* FOOTER */}
      <Box sx={{ flexShrink: 0, width: '100%' }}>
        <BaseDecarbFooterBar
          backText={'Cancel'}
          onBackClick={() => {
            // Navigate to proj summary page
            setWarningModalOpen(true);
          }}
          onNextClick={() => {
            handleSaveSubproject(
              {
                name: name.trim(),
                userSubprojId: userSubprojId.trim(),
                address: address.trim(),
                description: description.trim(),
                sector: 'Building', // hardcoded for now
                sponsoringDepts: sponsoringDeptIds,
                overseeingDepts: overseeingDeptIds,
                usingDepts: usingDeptIds,
                programManagers: progManagerIds,
                projectManagers: projManagerIds,
                budgetOfficers: budgetOfficerIds,
              },
              false,
            );
          }}
          nextIsDisabled={!name.trim() || !userSubprojId.trim() || isSaving}
          nextIsDisabledText={isSaving ? 'SAVING…' : 'INCOMPLETE DATA'}
          onReturnToHubClick={() => {
            handleSaveSubproject(
              {
                name: name.trim(),
                userSubprojId: userSubprojId.trim(),
                address: address.trim(),
                description: description.trim(),
                sector: 'Building', // hardcoded for now
                sponsoringDepts: sponsoringDeptIds,
                overseeingDepts: overseeingDeptIds,
                usingDepts: usingDeptIds,
                programManagers: progManagerIds,
                projectManagers: projManagerIds,
                budgetOfficers: budgetOfficerIds,
              },
              true,
            );
          }}
          returnText={
            pencilsDown ? 'RETURN TO SUMMARY' : 'SAVE & EXIT TO SUMMARY'
          }
        />
      </Box>
    </>
  );
}
